import mapValues from 'lodash/mapValues';

import { formatDateShort } from '~/services/TimeService';

import { parseIsoDateUtc } from '../tabs/surcharges/computeNightlySurcharge';

import type { ImportSection, SheetContext, SheetRows } from './types';

const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 86_400_000;

export function andList(items: ReadonlyArray<string>): string {
  return items.length === 1 ? items[0] : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

export function cellAt(rows: SheetRows, row: number, col: number): unknown {
  return rows[row]?.[col] ?? null;
}

export function cellAddress(row: number, col: number): string {
  let letters = '';
  for (let n = col + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    letters = String.fromCharCode(65 + ((n - 1) % 26)) + letters;
  }
  return `${letters}${row + 1}`;
}

export function columnLetter(col: number): string {
  return cellAddress(0, col).replace(/\d+/g, '');
}

export function textOf(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function numberOf(value: unknown): number | null {
  const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof number === 'number' && Number.isFinite(number) ? number : null;
}

export function positiveNumberOf(value: unknown): number | null {
  const number = numberOf(value);
  return number !== null && number > 0 ? number : null;
}

// The tool shows and edits whole dollars, so a fraction would sit unseen under every figure built on it.
export function priceOf(value: unknown): number | null {
  const number = positiveNumberOf(value);
  return number === null ? null : Math.round(number) || null;
}

// Blank and 0 are how the template leaves a price nobody typed.
export function isUnpriced(value: unknown): boolean {
  return value === null || (typeof value === 'string' && value.trim() === '') || numberOf(value) === 0;
}

// read-excel-file returns an error cell as text, e.g. "#ERROR_#REF!".
export function excelErrorOf(value: unknown): string | null {
  const text = textOf(value);
  return text?.startsWith('#') ? text.replace(/^#ERROR_/, '') : null;
}

// What a cell that should hold a price shows instead: an Excel error by name, anything else as "isn't a price".
export function priceProblem(value: unknown, column: string): string {
  const error = excelErrorOf(value);
  return error ? `the ${column} shows ${error}` : `the ${column} isn't a price`;
}

export function unusablePriceProblem(value: unknown, column: string): string {
  return `${priceProblem(value, column)}.`;
}

interface BlankedCell {
  column: string;
  row: number;
  tab: string;
}

function blankedNote(problem: string, { row, tab }: BlankedCell): string {
  return `On row ${row}, ${problem}, so it loads blank. Add it on ${tab}.`;
}

// For a value the PCM can add on the tab later: an unusable cell loads blank, but says so rather than passing as empty.
export function blankedPriceNote(value: unknown, usable: number | null, where: BlankedCell): string | null {
  return usable !== null || isUnpriced(value) ? null : blankedNote(priceProblem(value, where.column), where);
}

// A note about a value the row still loads with, pushed only when there is one.
export function warnAt(
  context: SheetContext,
  section: ImportSection,
  row: number,
  col: number,
  note: string | null,
): boolean {
  if (note) {
    context.issues.push({ section, severity: 'warning', cell: cellAddress(row, col), message: note });
  }
  return !!note;
}

export function periodDates({ startDate, endDate }: { startDate: string; endDate: string }): string {
  return `${formatDateShort(startDate)} to ${formatDateShort(endDate)}`;
}

export function blankedErrorNote(value: unknown, where: BlankedCell): string | null {
  const error = excelErrorOf(value);
  return error ? blankedNote(`the ${where.column} shows ${error}`, where) : null;
}

export function isoDateOf(value: unknown): string | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  }
  // A date cell without a date format arrives as its serial number.
  if (typeof value === 'number' && value > 0) {
    return new Date(EXCEL_EPOCH_UTC + Math.round(value) * MS_PER_DAY).toISOString().slice(0, 10);
  }
  const text = textOf(value);
  // Strict like the period and blackout save checks, so 2027-02-30 is rejected here rather than on save.
  return text && parseIsoDateUtc(text) ? text : null;
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH_LABEL = /^([a-z]{3})-(\d{2})$/i;

// Text like "Oct-26" as most templates show it, or a real date cell.
export function monthKeyOf(value: unknown): string | null {
  const date = typeof value === 'string' ? null : isoDateOf(value);
  if (date) {
    return date.slice(0, 7);
  }
  const match = MONTH_LABEL.exec(textOf(value) ?? '');
  const month = match ? MONTHS.indexOf(match[1].toLowerCase()) : -1;
  return match && month !== -1 ? `20${match[2]}-${String(month + 1).padStart(2, '0')}` : null;
}

export function isBlank(value: unknown): boolean {
  return value === null || (typeof value === 'string' && textOf(value) === null);
}

function matchesLabel(value: unknown, label: string): boolean {
  return textOf(value)?.toLowerCase() === label.toLowerCase();
}

interface FindOptions {
  fromRow?: number;
  toRow?: number;
  col?: number;
  firstCol?: number;
  lastCol?: number;
}

export function findLabel(rows: SheetRows, label: string, options: FindOptions = {}) {
  const { fromRow = 0, toRow = rows.length - 1, col, firstCol = 0, lastCol: searchLastCol = Infinity } = options;

  for (let row = fromRow; row <= Math.min(toRow, rows.length - 1); row++) {
    const cells = rows[row] ?? [];
    const lastCol = col ?? Math.min(searchLastCol, cells.length - 1);
    for (let c = col ?? firstCol; c <= lastCol; c++) {
      if (matchesLabel(cells[c], label)) {
        return { row, col: c };
      }
    }
  }
  return null;
}

// Every column is required: a section read without one would load half its data without anyone noticing.
export function findImportColumns<Column extends string>(
  { rows, sheet, issues }: SheetContext,
  labels: Record<Column, string>,
  section: ImportSection,
  noun: string,
): Record<Column, number> | null {
  const headerCol = (label: string) => findLabel(rows, label, { toRow: 0 })?.col;
  const missing = Object.values<string>(labels).find((label) => headerCol(label) === undefined);
  if (missing) {
    issues.push({
      section,
      severity: 'error',
      message: `The "${sheet}" sheet has no "${missing}" column, so no ${noun} will load.`,
    });
    return null;
  }
  return mapValues(labels, headerCol);
}
